import React, { useCallback, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../../common/PrimaryActionButton';
import {
  CUSTOMER_IMPORT_TARGET_FIELDS,
  canContinueCustomerImportMapping,
} from '../../../utils/customerFileImport';
import CustomerImportMappingPair from './CustomerImportMappingPair';
import CustomerImportMappingSelectForm from './CustomerImportMappingSelectForm';
import CustomerImportMappingTile from './CustomerImportMappingTile';
import { BRAND, brandAlpha } from '../../../theme/brandTokens';

const SOURCE_MARKER = brandAlpha('accent', 0.6);
const REQUIRED_MARKER = BRAND.accent;
const OPTIONAL_MARKER = BRAND.accentDark;

const buildId = (type, field) => `${type}:${encodeURIComponent(field)}`;

const parseId = (id) => {
  const [type, encoded] = String(id).split(':');
  if (!type || encoded === undefined || (type !== 'file' && type !== 'target')) {
    return null;
  }
  return { type, field: decodeURIComponent(encoded) };
};

const FieldTile = ({
  id,
  label,
  markerSide,
  markerColor,
  disabled,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, disabled });
  const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({ id });

  const setNodeRefs = useCallback((node) => {
    setNodeRef(node);
    setDroppableNodeRef(node);
  }, [setDroppableNodeRef, setNodeRef]);

  let opacity = 1;
  if (disabled) {
    opacity = 0.55;
  } else if (isDragging) {
    opacity = 0.3;
  }

  return (
    <Box
      ref={setNodeRefs}
      {...attributes}
      {...listeners}
      sx={{
        width: '100%',
        opacity,
        cursor: disabled ? 'not-allowed' : 'grab',
      }}
    >
      <CustomerImportMappingTile
        label={label}
        markerSide={markerSide}
        markerColor={markerColor}
        disabled={disabled}
        highlighted={isOver}
      />
    </Box>
  );
};

const DragPreview = ({ activeId, getTargetLabel }) => {
  if (!activeId) {
    return null;
  }

  const activeItem = parseId(activeId);
  if (!activeItem) {
    return null;
  }

  const isSource = activeItem.type === 'file';
  const isRequired = CUSTOMER_IMPORT_TARGET_FIELDS.some(
    (field) => field.id === activeItem.field && field.required,
  );
  let markerColor = OPTIONAL_MARKER;
  if (isSource) {
    markerColor = SOURCE_MARKER;
  } else if (isRequired) {
    markerColor = REQUIRED_MARKER;
  }

  return (
    <CustomerImportMappingTile
      label={isSource ? activeItem.field : getTargetLabel(activeItem.field)}
      markerSide={isSource ? 'left' : 'right'}
      markerColor={markerColor}
      disabled={false}
    />
  );
};

const CustomerImportMappingBoard = ({
  fileName,
  sourceFields,
  initialMappings,
  onCancel,
  onContinue,
}) => {
  const { t } = useTranslation();
  const useDragMapping = useMediaQuery('(hover: hover) and (pointer: fine)', { noSsr: true });
  const [localMappings, setLocalMappings] = useState(initialMappings || []);
  const [activeId, setActiveId] = useState(null);

  const mappedSourceFields = useMemo(
    () => new Set(localMappings.map((mapping) => mapping.sourceField)),
    [localMappings],
  );
  const mappedTargetFields = useMemo(
    () => new Set(localMappings.map((mapping) => mapping.targetField)),
    [localMappings],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const getTargetLabel = useCallback(
    (field) => t(`customersManagement.import.fields.${field}`, { defaultValue: field }),
    [t],
  );

  const handleDragStart = ({ active }) => {
    setActiveId(active.id);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    const activeItem = parseId(active.id);
    const overItem = parseId(over.id);
    if (!activeItem || !overItem || activeItem.type === overItem.type) {
      setActiveId(null);
      return;
    }

    const sourceField = activeItem.type === 'file' ? activeItem.field : overItem.field;
    const targetField = activeItem.type === 'target' ? activeItem.field : overItem.field;

    if (!mappedSourceFields.has(sourceField) && !mappedTargetFields.has(targetField)) {
      setLocalMappings((current) => [...current, { sourceField, targetField }]);
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleRemoveMapping = (sourceField, targetField) => {
    setLocalMappings((current) => current.filter(
      (mapping) => mapping.sourceField !== sourceField || mapping.targetField !== targetField,
    ));
  };

  const handleSelectMapping = (targetField, sourceField) => {
    setLocalMappings((current) => {
      const withoutTarget = current.filter((mapping) => mapping.targetField !== targetField);
      if (!sourceField) {
        return withoutTarget;
      }

      return [
        ...withoutTarget.filter((mapping) => mapping.sourceField !== sourceField),
        { sourceField, targetField },
      ];
    });
  };

  const columnScrollSx = {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    pr: 0.5,
  };

  const requiredFields = CUSTOMER_IMPORT_TARGET_FIELDS.filter((field) => field.required);
  const optionalFields = CUSTOMER_IMPORT_TARGET_FIELDS.filter((field) => !field.required);
  const canContinue = canContinueCustomerImportMapping(localMappings);

  const mappingActions = (
    <>
      <Button color="inherit" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
      <PrimaryActionButton
        onClick={() => onContinue(localMappings)}
        disabled={!canContinue}
      >
        {t('customersManagement.import.continue')}
      </PrimaryActionButton>
    </>
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: brandAlpha('text', 0.55),
        zIndex: 1300,
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 0, md: 2 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1664,
          height: { xs: '100%', md: '90vh' },
          maxHeight: { xs: '100%', md: 'calc(100vh - 32px)' },
          bgcolor: '#f5f5f5',
          borderRadius: { xs: 0, md: '12px' },
          p: { xs: 0, md: 2 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Paper
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#fff',
            borderRadius: { xs: 0, md: '8px' },
            boxShadow: `0 8px 32px ${brandAlpha('text', 0.12)}`,
            p: { xs: 2, sm: 3, md: 4 },
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 3,
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: BRAND.text }}>
                {t('customersManagement.import.mappingTitle')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  mt: 1,
                  maxWidth: 560,
                }}
              >
                {t(useDragMapping
                  ? 'customersManagement.import.mappingSubtitle'
                  : 'customersManagement.import.mappingSelectSubtitle')}
              </Typography>
            </Box>
            <IconButton
              aria-label={t('common.close')}
              onClick={onCancel}
              size="small"
              sx={{
                bgcolor: '#fff',
                border: `1px solid ${BRAND.line}`,
                width: 40,
                height: 40,
                borderRadius: '12px',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <CloseIcon sx={{ fontSize: 18, color: '#757575' }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              alignItems: { xs: 'stretch', md: 'center' },
              mt: 3,
              mb: { xs: 2, md: 4 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#fff',
                  border: `1px solid ${BRAND.line}`,
                  borderRadius: '12px',
                  minHeight: 39,
                  overflow: 'hidden',
                  maxWidth: 400,
                }}
              >
                <Box sx={{ width: 4, alignSelf: 'stretch', bgcolor: BRAND.accent }} />
                <Box sx={{ px: 2, py: 1.25, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {fileName}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5, justifyContent: 'flex-end' }}>
              {mappingActions}
            </Box>
          </Box>

          <Divider sx={{ flexShrink: 0, borderColor: BRAND.line }} />

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {useDragMapping ? (
              <DndContext
                sensors={sensors}
                collisionDetection={rectIntersection}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)',
                  gap: 6,
                  mt: 3,
                  borderRadius: '12px',
                  p: 3,
                  bgcolor: '#fff',
                  backgroundImage: `radial-gradient(circle, ${brandAlpha('text', 0.08)} 1px, transparent 1px)`,
                  backgroundSize: '14px 14px',
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                  <Typography sx={{ flexShrink: 0, fontSize: '1rem', fontWeight: 700, color: BRAND.text, mb: 2 }}>
                    {t('customersManagement.import.fileColumns')}
                  </Typography>
                  <Stack spacing={2} sx={{ ...columnScrollSx, alignItems: 'center' }}>
                    {sourceFields.map((field) => (
                      <FieldTile
                        key={field}
                        id={buildId('file', field)}
                        label={field}
                        markerSide="left"
                        markerColor={SOURCE_MARKER}
                        disabled={mappedSourceFields.has(field)}
                      />
                    ))}
                  </Stack>
                </Box>

                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                  <Typography sx={{ flexShrink: 0, fontSize: '1rem', fontWeight: 700, color: BRAND.text, mb: 2 }}>
                    {t('customersManagement.import.mapping')}
                  </Typography>
                  <Stack spacing={2} sx={{ ...columnScrollSx, width: '100%' }}>
                    {localMappings.map((mapping) => (
                      <CustomerImportMappingPair
                        key={`${mapping.sourceField}-${mapping.targetField}`}
                        sourceLabel={mapping.sourceField}
                        targetLabel={getTargetLabel(mapping.targetField)}
                        onClick={() => handleRemoveMapping(mapping.sourceField, mapping.targetField)}
                      />
                    ))}
                  </Stack>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                  <Typography sx={{ flexShrink: 0, fontSize: '1rem', fontWeight: 700, color: BRAND.text, mb: 2 }}>
                    {t('customersManagement.import.salovareFields')}
                  </Typography>
                  <Stack spacing={2} sx={{ ...columnScrollSx, alignItems: 'center', width: '100%' }}>
                    {requiredFields.map((field) => (
                      <FieldTile
                        key={field.id}
                        id={buildId('target', field.id)}
                        label={getTargetLabel(field.id)}
                        markerSide="right"
                        markerColor={REQUIRED_MARKER}
                        disabled={mappedTargetFields.has(field.id)}
                      />
                    ))}
                    {optionalFields.map((field) => (
                      <FieldTile
                        key={field.id}
                        id={buildId('target', field.id)}
                        label={getTargetLabel(field.id)}
                        markerSide="right"
                        markerColor={OPTIONAL_MARKER}
                        disabled={mappedTargetFields.has(field.id)}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
              <DragOverlay>
                <DragPreview activeId={activeId} getTargetLabel={getTargetLabel} />
              </DragOverlay>
              </DndContext>
            ) : (
              <Box sx={{ maxWidth: 560, width: '100%', flex: 1, minHeight: 0, display: 'flex' }}>
                <CustomerImportMappingSelectForm
                  sourceFields={sourceFields}
                  mappings={localMappings}
                  onChangeMapping={handleSelectMapping}
                />
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexShrink: 0,
              justifyContent: 'space-between',
              gap: 1.5,
              pt: 2,
              mt: 1,
              borderTop: `1px solid ${BRAND.line}`,
            }}
          >
            {mappingActions}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CustomerImportMappingBoard;
